import {
  ATTACHMENT_MIME_TYPE,
  ATTACHMENT_MIME_TYPE_LIST,
  MAX_SIZE_KB
} from '@/constants/attachments';

/**
 * Attachment Class.
 *
 * @module Attachment
 **/
export default class Attachment {

  /**
   * @constructor
   * @this {Attachment}
   *
   * @param {Object} RawAttachment - Attachment information.
   **/
  constructor({ content, contentKey, contentName, contentSize, contentType, messageId, _content}) {
    this._content = (_content || content) ? (_content || content).replace(/data:(.*);base64,/i, '') : '';
    this.contentKey = contentKey;
    this.contentName = contentName;
    this.contentSize = contentSize;
    this.contentType = contentType;
    this.messageId = messageId;
  }

  /**
   * Public getters
   */

  get isVCF() {
    return ATTACHMENT_MIME_TYPE.vcf.includes(this.contentType)
      || (ATTACHMENT_MIME_TYPE.text.includes(this.contentType) && this.contentName.slice(-4) === '.vcf');
  }

  get isImage() {
    return ATTACHMENT_MIME_TYPE.image.includes(this.contentType);
  }

  get isAudio() {
    return ATTACHMENT_MIME_TYPE.audio.includes(this.contentType);
  }

  get isVideo() {
    return ATTACHMENT_MIME_TYPE.video.includes(this.contentType);
  }

  get base64String() {
    return `data:${this.contentType};base64,${this.content}`;
  }

  get content() {
    return this._content;
  }

  /**
   * Setters
   */

  set content(content) {
    this._content = content ? content.replace(/data:(.*);base64,/i, '') : '';
  }

  /**
   * Public methods
   */

  download() {
    const file = new File(this._content, this.contentName, {
      type: this.contentType,
    });
    Attachment.download(file);
  }

  /**
   * Public static methods
   */

  static download(file) {
    const downloadLink = document.createElement('a');
    const blob = file.fileSRC;
    const url = window.URL.createObjectURL(blob);

    document.body.appendChild(downloadLink);
    downloadLink.style.display = 'none';
    downloadLink.href = url;
    downloadLink.download = file.name;
    downloadLink.click();

    window.URL.revokeObjectURL(url);
  }

  /**
   * Determines whether the value is a Base64 encoded string (RFC 4648) or not
   * returning true or false as appropriate.
   *
   * @param {String} value
   *
   * @returns {Boolean}
   */
  static isBase64(value) {
    // eslint-disable-next-line
    return /^(?:[a-zA-Z0-9+\/]{4})*(?:|(?:[a-zA-Z0-9+\/]{3}=)|(?:[a-zA-Z0-9+\/]{2}==)|(?:[a-zA-Z0-9+\/]{1}===))$/.test(value);
  }

  /**
   * Converts a File instance into a Base64 encoded string.
   *
   * @param {File} file
   *
   * @returns {Promise<String>}
   */
  static fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  static isValidFileType(mimeType) {
    return ATTACHMENT_MIME_TYPE_LIST.includes(mimeType);
  }

  static isValidFileSize(fileSizeKb, maxFileSizeKb = MAX_SIZE_KB.FILE) {
    return fileSizeKb / 1024 <= maxFileSizeKb;
  }
}
